"use client";

import React from "react";
import Image from "next/image";
import { Box } from "@mui/material";
import { ColletteCarousel } from "@collette-travel/collette-common-controls";
import { Award } from "@/types/dto/homepage/homepage-types";

interface AwardsScrollProps {
  awardsData?: Award[];
  dataModuleName?: string;
  dataRankingAlgorithm?: string;
  dataModuleRankingPosition?: string;
  dataTestid?: string;
}
const AwardImage = React.forwardRef<
  HTMLImageElement,
  {
    src: string;
    alt: string;
    testId?: string;
  }
>((props, ref) => (
  <span style={{ display: "inline-flex", height: "60px", width: "auto" }}>
    <Image
      ref={ref}
      src={props.src}
      alt={props.alt}
      data-testid={props.testId}
      loading="lazy"
      height={60}
      width={120}
      style={{
        height: "60px",
        width: "auto",
        objectFit: "contain",
        filter: "grayscale(100%) brightness(1.1)",
        opacity: 0.8,
        flexShrink: 0
      }}
      unoptimized={false}
    />
  </span>
));

AwardImage.displayName = "AwardImage";

const AwardsScroll: React.FC<AwardsScrollProps> = ({
  awardsData,
  dataModuleName = "Awards Scroll Section",
  dataRankingAlgorithm = "manual",
  dataModuleRankingPosition = "6",
  dataTestid = "homepage-awards-section-root"
}) => {
  if (!awardsData || awardsData.length === 0) return null;

  // Create more duplicates to fill the viewport and prevent white space
  const multipliedAwards = Array(6).fill(awardsData).flat();

  return (
      <Box
        component="section"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "background.default"
        }}
        data-track="visibility"
        data-module-name={dataModuleName}
        data-ranking-algorithm={dataRankingAlgorithm}
        data-module-ranking-position={dataModuleRankingPosition}
        data-testid={dataTestid}
      >
        <ColletteCarousel
          testId="awards-scroll-carousel"
          sideBlur={false}
          variant="multi-card"
          arrowPosition="none"
          align="start"
          slidesToScroll="auto"
          spacing={60}
          loop
          autoScroll
          dragFree
          skipSnaps
          arrowContainerSx={{ marginRight: "70px" }}
          autoScrollSpeed={1.2}
        >
          {multipliedAwards.map((award, index) => (
            <AwardImage
              key={`${award.id}-${Math.floor(index / awardsData.length)}-${
                index % awardsData.length
              }`}
              src={award.imageUrl}
              alt={award.altText}
              testId={`award-image-${award.altText}-${index}`}
            />
          ))}
        </ColletteCarousel>
      </Box>
  );
};

export default AwardsScroll;
